import type { ShareItem, HisseDurum } from "../../../../mocks/animals";
import PhoneActionMenu from "../../../../components/contact/PhoneActionMenu";

interface Props {
  hisseler: ShareItem[];
}

const hisseBadge: Record<HisseDurum, { cls: string; label: string }> = {
  Tam: { cls: "bg-farm-green-pale text-farm-green", label: "Tam" },
  Kısmi: { cls: "bg-farm-gold-pale text-farm-gold", label: "Kısmi" },
  Bekliyor: { cls: "bg-farm-earth-pale text-farm-earth", label: "Bekliyor" },
};

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

function toplamUcret(h: ShareItem) {
  return (
    h.tutar +
    (h.kesimUcreti ?? 0) +
    (h.parcalamaUcreti ?? 0) +
    (h.tasimaUcreti ?? 0) -
    (h.indirim ?? 0)
  );
}

export default function ShareExpandPanel({ hisseler }: Props) {
  if (!hisseler.length) {
    return (
      <div className="px-3 py-3 text-center">
        <p className="font-body text-xs text-farm-muted italic">
          Henüz hisse kaydı yok.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-farm-cream border-t border-farm-border px-2 py-2">
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-xs font-body min-w-[1320px]">
          <thead>
            <tr className="text-farm-muted border-b border-farm-border">
              <th className="py-1.5 px-2 text-left font-semibold whitespace-nowrap">Hisse No</th>
              <th className="py-1.5 px-2 text-left font-semibold whitespace-nowrap">Müşteri</th>
              <th className="py-1.5 px-2 text-left font-semibold whitespace-nowrap">Telefon</th>
              <th className="py-1.5 px-2 text-center font-semibold whitespace-nowrap">Hisse</th>
              <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">Hisse Satış</th>
              <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">Kesim</th>
              <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">Parçalama</th>
              <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">Taşıma</th>
              <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">İndirim</th>
              <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">Toplam Ücret</th>
              <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">Ödenen</th>
              <th className="py-1.5 px-2 text-right font-semibold whitespace-nowrap">Kalan</th>
              <th className="py-1.5 px-2 text-left font-semibold whitespace-nowrap">Satış Notu</th>
              <th className="py-1.5 px-2 text-center font-semibold whitespace-nowrap">Durum</th>
            </tr>
          </thead>
          <tbody>
            {hisseler.map((h) => {
              const badge = hisseBadge[h.durum];
              return (
                <tr
                  key={h.id}
                  className="border-b border-farm-border/50 hover:bg-farm-earth-pale/30 transition-colors"
                >
                  <td className="py-1.5 px-2 text-farm-brown font-medium whitespace-nowrap">
                    {h.hisseNo}
                  </td>
                  <td className="py-1.5 px-2 text-farm-brown whitespace-nowrap">
                    {h.musteriAdi}
                  </td>
                  <td className="py-1.5 px-2 text-farm-muted whitespace-nowrap">
                    <PhoneActionMenu phone={h.telefon} buttonClassName="font-semibold text-farm-earth hover:text-farm-brown" />
                  </td>
                  <td className="py-1.5 px-2 text-center text-farm-muted">
                    {h.hisseSayisi}
                  </td>
                  <td className="py-1.5 px-2 text-right text-farm-brown whitespace-nowrap">
                    {fmt(h.tutar)}
                  </td>
                  <td className="py-1.5 px-2 text-right text-farm-brown whitespace-nowrap">
                    {fmt(h.kesimUcreti ?? 0)}
                  </td>
                  <td className="py-1.5 px-2 text-right text-farm-brown whitespace-nowrap">
                    {fmt(h.parcalamaUcreti ?? 0)}
                  </td>
                  <td className="py-1.5 px-2 text-right text-farm-brown whitespace-nowrap">
                    {fmt(h.tasimaUcreti ?? 0)}
                  </td>
                  <td className="py-1.5 px-2 text-right whitespace-nowrap">
                    <span className={(h.indirim ?? 0) > 0 ? "text-farm-earth font-semibold" : "text-farm-muted"}>
                      {(h.indirim ?? 0) > 0 ? `-${fmt(h.indirim ?? 0)}` : "—"}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-right text-farm-brown font-semibold whitespace-nowrap">
                    {fmt(toplamUcret(h))}
                  </td>
                  <td className="py-1.5 px-2 text-right text-farm-green whitespace-nowrap">
                    {fmt(h.odenen)}
                  </td>
                  <td
                    className={`py-1.5 px-2 text-right font-semibold whitespace-nowrap ${
                      h.kalan > 0 ? "text-farm-earth" : "text-farm-muted"
                    }`}
                  >
                    {fmt(h.kalan)}
                  </td>
                  <td className="py-1.5 px-2 text-farm-brown max-w-[280px]">
                    <div className="truncate" title={h.satisNotu || "—"}>{h.satisNotu || "—"}</div>
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`}>
                      {badge.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden flex flex-col gap-1.5">
        {hisseler.map((h) => {
          const badge = hisseBadge[h.durum];

          return (
            <div
              key={h.id}
              className="bg-farm-warm border border-farm-border rounded p-2.5 flex flex-col gap-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-body text-[10px] text-farm-muted">{h.hisseNo}</span>
                    <span className="font-body text-xs font-semibold text-farm-brown">
                      {h.musteriAdi}
                    </span>
                  </div>
                  {h.telefon && (
                    <p className="font-body text-[10px] text-farm-muted mt-0.5">
                      <PhoneActionMenu phone={h.telefon} buttonClassName="font-semibold text-farm-earth hover:text-farm-brown" />
                    </p>
                  )}
                </div>

                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`}>
                  {badge.label}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] font-body mt-0.5">
                <div>
                  <p className="text-farm-muted">Hisse</p>
                  <p className="text-farm-brown font-semibold">{h.hisseSayisi}</p>
                </div>
                <div>
                  <p className="text-farm-muted">Hisse Satış</p>
                  <p className="text-farm-brown font-semibold">{fmt(h.tutar)}</p>
                </div>
                <div>
                  <p className="text-farm-muted">Kesim</p>
                  <p className="text-farm-brown font-semibold">{fmt(h.kesimUcreti ?? 0)}</p>
                </div>
                <div>
                  <p className="text-farm-muted">Parçalama</p>
                  <p className="text-farm-brown font-semibold">{fmt(h.parcalamaUcreti ?? 0)}</p>
                </div>
                <div>
                  <p className="text-farm-muted">Taşıma</p>
                  <p className="text-farm-brown font-semibold">{fmt(h.tasimaUcreti ?? 0)}</p>
                </div>
                <div>
                  <p className="text-farm-muted">İndirim</p>
                  <p className={`font-semibold ${(h.indirim ?? 0) > 0 ? "text-farm-earth" : "text-farm-muted"}`}>
                    {(h.indirim ?? 0) > 0 ? `-${fmt(h.indirim ?? 0)}` : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-farm-muted">Toplam Ücret</p>
                  <p className="text-farm-brown font-semibold">{fmt(toplamUcret(h))}</p>
                </div>
                <div>
                  <p className="text-farm-muted">Ödenen</p>
                  <p className="text-farm-green font-semibold">{fmt(h.odenen)}</p>
                </div>
                <div>
                  <p className="text-farm-muted">Kalan</p>
                  <p className={`font-semibold ${h.kalan > 0 ? "text-farm-earth" : "text-farm-muted"}`}>
                    {fmt(h.kalan)}
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-farm-border bg-white/60 px-2 py-1.5">
                <p className="text-[10px] text-farm-muted">Satış Notu</p>
                <p className="mt-0.5 text-[11px] text-farm-brown">{h.satisNotu || "—"}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
